import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/authService';

// Check if user is already authenticated
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔍 checkAuth: Starting authentication check...');
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.log('❌ checkAuth: No token found in sessionStorage');
        return rejectWithValue('No token found');
      }

      console.log('✅ checkAuth: Token found, validating...');
      console.log('🔑 checkAuth: Token preview:', token.substring(0, 20) + '...');

      // Validate token by getting user info
      const userData = await authService.getUserInfo();
      console.log('✅ checkAuth: User data retrieved successfully:', userData);
      console.log('🎉 checkAuth: Authentication check completed successfully');
      return { token, user: userData };
    } catch (error) {
      console.error('💥 checkAuth: Authentication check failed!');
      console.error('💥 checkAuth: Error details:', error);
      console.error('💥 checkAuth: Error message:', error.message);
      console.error('💥 checkAuth: Error response:', error.response);

      // Only remove token if it's actually invalid (401), not for network errors
      if (error.message === 'Invalid or expired token' ||
          (error.response && error.response.status === 401)) {
        console.error('🗑️ checkAuth: Removing invalid token from sessionStorage');
        sessionStorage.removeItem('token');
      } else {
        console.error('⚠️ checkAuth: Keeping token - error was not 401/invalid token');
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('authSlice: Attempting login for user:', username);
        console.log('authSlice: Environment:', process.env.NODE_ENV);
        console.log('authSlice: API URL:', process.env.REACT_APP_API_URL);
      }

      const response = await authService.login(username, password);
      if (process.env.NODE_ENV === 'development') {
        console.log('authSlice: Login response received:', response);
      }

      if (response.token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('authSlice: Token received, storing in sessionStorage');
        }
        sessionStorage.setItem('token', response.token);
      } else {
        console.error('authSlice: No token received in login response');
      }

      return response;
    } catch (error) {
      console.error('authSlice: Login failed:', error);
      if (error.response) {
        console.error('authSlice: Error response:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('authSlice: No response received from server');
      } else {
        console.error('authSlice: Error setting up request:', error.message);
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Logout user
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    sessionStorage.removeItem('token');

    // Clear all monitoring data including filmstrip state
    const { clearAllMonitoringData } = await import('./monitoringSlice');
    dispatch(clearAllMonitoringData());

    if (process.env.NODE_ENV === 'development') {
      console.log('🚪 Logout: Cleared sessionStorage token and all monitoring data');
    }

    return null;
  }
);

const initialState = {
  token: sessionStorage.getItem('token') || null,
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('checkAuth fulfilled with payload:', action.payload);
        }
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth state updated:', {
            isAuthenticated: state.isAuthenticated,
            user: state.user
          });
        }
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload;
      })

      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('login fulfilled with payload:', action.payload);
        }
        state.loading = false;

        // Only set authenticated if we actually received a token
        if (action.payload && action.payload.token) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
          state.user = action.payload.user;
          state.error = null;
          if (process.env.NODE_ENV === 'development') {
            console.log('Auth state updated after successful login:', {
              isAuthenticated: state.isAuthenticated,
              user: state.user
            });
            console.log('User should be redirected to dashboard now');
          }
        } else {
          // No token received, treat as failed login
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          state.error = action.payload?.message || 'Login failed - no token received';
          if (process.env.NODE_ENV === 'development') {
            console.log('Login fulfilled but no token received, treating as failed login');
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      });
  },
});

export const { clearError } = authSlice.actions;

export default authSlice.reducer;
